import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SectionHeader from '../components/SectionHeader';
import StatCard from '../components/StatCard';
import { SOBlock, formatDateNZ, todayISO } from '../components/BlockedOrdersShared';

// ── Page ──────────────────────────────────────────────────────────────────────
const BackOrderFollowUpSchedule = () => {
  const navigate = useNavigate();
  const [orders, setOrders]           = useState([]);
  const [notesByOrder, setNotesByOrder] = useState({});
  const [assignedByOrder, setAssignedByOrder] = useState({});
  const [staffList, setStaffList]     = useState([]);
  const [nextActions, setNextActions] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [staffFilter, setStaffFilter] = useState('');

  const loadStaff = useCallback(async () => {
    const { data } = await supabase.from('staff').select('name, initials').order('name');
    setStaffList(data || []);
  }, []);

  const loadNextActions = useCallback(async () => {
    const { data } = await supabase
      .from('blocked_order_next_actions')
      .select('id, label, sort_order')
      .eq('active', true)
      .order('sort_order');
    setNextActions(data || []);
  }, []);

  const loadNotes = useCallback(async () => {
    const { data } = await supabase
      .from('blocked_order_notes')
      .select('id, order_id, note, initials, created_at, follow_up_date')
      .order('created_at', { ascending: false });
    const map = {};
    for (const n of data || []) {
      (map[n.order_id] ??= []).push(n);
    }
    setNotesByOrder(map);
  }, []);

  const loadAssignments = useCallback(async () => {
    const { data } = await supabase
      .from('blocked_order_assignments')
      .select('order_id, initials, urgent');
    const map = {};
    for (const a of data || []) {
      map[a.order_id] = { initials: a.initials, urgent: a.urgent };
    }
    setAssignedByOrder(map);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);

    const { data, error } = await supabase.rpc('get_backorder_worklist');

    if (error) {
      console.error('get_backorder_worklist error:', error);
      setOrders([]);
      setLoading(false);
      return;
    }

    const rows = data || [];

    const orderMap = new Map();
    for (const row of rows) {
      if (!orderMap.has(row.order_id)) {
        orderMap.set(row.order_id, {
          order_id:     row.order_id,
          order_ref:    row.order_ref,
          created_date: row.created_date,
          stage:        row.stage,
          status:       row.status,
          first_name:   row.first_name || '',
          last_name:    row.last_name  || '',
          email:        row.email || '',
          phone:        row.phone || '',
          internal_comments: row.internal_comments || '',
          items:        [],
        });
      }
      if (row.item_code) {
        orderMap.get(row.order_id).items.push({
          item_code:   row.item_code,
          item_name:   row.item_name || row.item_code,
          qty_ordered: row.qty_ordered,
          qty_moved:   row.qty_moved ?? 0,
          po_number:   row.po_number,
        });
      }
    }

    setOrders([...orderMap.values()]);
    setLoading(false);
    setLastRefresh(new Date());
  }, []);

  useEffect(() => {
    load(); loadNotes(); loadStaff(); loadAssignments(); loadNextActions();
  }, [load, loadNotes, loadStaff, loadAssignments, loadNextActions]);

  const handleNoteAdded = (orderId, newNote) => {
    setNotesByOrder((prev) => ({
      ...prev,
      [orderId]: [newNote, ...(prev[orderId] || [])],
    }));
  };

  const handleAssign = (orderId, initials) => {
    setAssignedByOrder((prev) => ({ ...prev, [orderId]: { ...(prev[orderId] || {}), initials } }));
  };

  const handleUrgentChange = (orderId, urgent) => {
    setAssignedByOrder((prev) => ({ ...prev, [orderId]: { ...(prev[orderId] || {}), urgent } }));
  };

  // The order's current follow-up date is whatever its most recent note set —
  // notesByOrder is sorted newest-first, so [0] is the live schedule.
  const followUpFor = (orderId) => notesByOrder[orderId]?.[0]?.follow_up_date || null;
  const today = todayISO();

  // Due-today and overdue belong exclusively on Back Order Management now --
  // this page is just what's coming up later.
  const scheduled = orders
    .filter(o => followUpFor(o.order_id) > today)
    .filter(o => {
      if (!staffFilter) return true;
      const initials = assignedByOrder[o.order_id]?.initials || '';
      if (staffFilter === '__unassigned__') return !initials;
      return initials === staffFilter;
    })
    .sort((a, b) => followUpFor(a.order_id).localeCompare(followUpFor(b.order_id)));

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/')}>← Home</button>
        <span className="page-nav__title">Back Order Follow-Up Schedule</span>
        <button className="back-btn" onClick={() => navigate('/backorder-management')}>📥 Back Order Management</button>
        <span className="app-header__refresh">Last refresh: {lastRefresh.toLocaleTimeString('en-NZ')}</span>
        <button className="app-header__btn" onClick={load}>↻ Refresh</button>
      </div>

      <div className="stats-row">
        <StatCard label="Upcoming" value={scheduled.length} accent="blue" />
      </div>

      <section className="section">
        <SectionHeader
          title="Follow-Up Schedule"
          count={scheduled.length}
          onRefresh={load}
          loading={loading}
          actions={
            <select
              value={staffFilter}
              onChange={e => setStaffFilter(e.target.value)}
              style={{
                background: 'var(--bg3)', color: 'var(--white)',
                border: '1px solid var(--border2)', borderRadius: 4,
                padding: '4px 10px', fontFamily: 'var(--font-body)',
                fontSize: 12, cursor: 'pointer',
              }}
            >
              <option value="">All Staff</option>
              <option value="__unassigned__">Unassigned</option>
              {staffList.map((s) => (
                <option key={s.initials} value={s.initials}>{s.name} ({s.initials})</option>
              ))}
            </select>
          }
        />
        <p className="section__desc">
          Back orders with a follow-up date scheduled for a future date — soonest first. Due-today and overdue show on Back Order Management instead. Same notes log as Back Order Management.
        </p>

        {loading && <div className="loading-row">Loading…</div>}
        {!loading && !scheduled.length && (
          <div className="empty-row">✓ No follow-ups scheduled</div>
        )}
        {!loading && scheduled.map(order => {
          const dueDate = followUpFor(order.order_id);
          return (
            <SOBlock
              key={order.order_id}
              order={order}
              notes={notesByOrder[order.order_id] || []}
              staffList={staffList}
              nextActions={nextActions}
              assignedInitials={assignedByOrder[order.order_id]?.initials}
              urgent={assignedByOrder[order.order_id]?.urgent}
              onNoteAdded={handleNoteAdded}
              onAssign={handleAssign}
              onUrgentChange={handleUrgentChange}
              extraHeader={
                <span className="pill pill--muted" title="Follow-up date">
                  Follow up: {formatDateNZ(dueDate)}
                </span>
              }
            />
          );
        })}
      </section>
    </main>
  );
};

export default BackOrderFollowUpSchedule;
